// Toast Store - Estado global de toasts gestionado con Zustand (H-F001)
// Migrado de variables de modulo mutables a un store Zustand dedicado,
// coherente con el resto del proyecto (authStore, workflowsStore, etc.)
import { useCallback } from 'react';
import { create } from 'zustand';

export type ToastType = 'success' | 'error' | 'info' | 'warning';

export interface Toast {
  id: string;
  message: string;
  type: ToastType;
  duration?: number;
}

let toastIdCounter = 0;

interface ToastStoreState {
  toasts: Toast[];
  addToast: (toast: Toast) => void;
  removeToast: (id: string) => void;
}

export const useToastStore = create<ToastStoreState>((set) => ({
  toasts: [],
  addToast: (toast: Toast) =>
    set((state) => ({ toasts: [...state.toasts, toast] })),
  removeToast: (id: string) =>
    set((state) => ({ toasts: state.toasts.filter((t) => t.id !== id) })),
}));

export function useToast() {
  const toasts = useToastStore((state) => state.toasts);
  const addToast = useToastStore((state) => state.addToast);
  const removeToastAction = useToastStore((state) => state.removeToast);

  const showToast = useCallback((message: string, type: ToastType = 'info', duration?: number) => {
    const id = `toast-${Date.now()}-${++toastIdCounter}`;
    const newToast: Toast = { id, message, type, duration };
    addToast(newToast);
  }, [addToast]);

  const removeToast = useCallback((id: string) => {
    removeToastAction(id);
  }, [removeToastAction]);

  return {
    toasts,
    showToast,
    removeToast,
    success: (message: string, duration?: number) => showToast(message, 'success', duration),
    error: (message: string, duration?: number) => showToast(message, 'error', duration),
    info: (message: string, duration?: number) => showToast(message, 'info', duration),
    warning: (message: string, duration?: number) => showToast(message, 'warning', duration),
  };
}
